import { useChatStore } from '@/store/useChatStore';
import {
  appendWithMaxAndNotice,
  trimTailWithNotice,
  stringifyClaudeOutput,
} from './agentChatUtils';

/**
 * StreamingManager - 流式输出管理器 (单例类)
 * 职责：负责 Assistant 文本流、Log 运行日志流、Collapsible 折叠块的缓冲、防抖刷新与强制截断。
 * 设计：采用单例模式，内部维护各种流的缓冲区和定时器，绕过 React 渲染频率限制，确保高性能输出。
 */
class StreamingManager {
  // --- Assistant 文本流状态 ---
  private assistantId: string | null = null; // 正在流式输出的 Assistant 消息 ID
  private assistantBuffer = ''; // 文本缓冲区
  private assistantTruncated = false; // 标记：内容是否已达到最大长度被截断
  private assistantTimer: NodeJS.Timeout | null = null; // 刷新定时器

  // --- Log 运行日志流状态 ---
  private logId: string | null = null; // 正在累加的 Log 消息 ID
  private logBuffer: string[] = []; // 日志缓冲区（数组形式提高性能）
  private logTimer: NodeJS.Timeout | null = null; // 定时刷新定时器
  private logEndTimer: NodeJS.Timeout | null = null; // 自动结束判定定时器

  // --- 折叠块状态 (用于处理大块输出内容) ---
  private collapsibleId: string | null = null;
  private collapsibleLastUpdate = 0; // 上次更新时间戳
  private readonly COLLAPSIBLE_RESET_MS = 500; // 超时 500ms 则认为新消息开始

  // --- 全局配置项 ---
  private config = {
    maxStreamingChars: 50_000, // AI 回复的最大字符数
    maxLogChars: 200_000, // 日志消息的最大字符数
    maxCollapsibleChars: 200_000, // 折叠块的最大字符数
    streamFlushInterval: 50, // AI 文本流刷新频率 (ms)
    logFlushInterval: 100, // 日志刷新频率 (ms)
  };

  /**
   * 外部调用：向 Assistant 消息追加一个文本块 (Chunk)
   */
  public appendStreamChunk(chunk: string) {
    if (!chunk) return;
    if (this.assistantTruncated) return; // 如果已截断，不再接受新内容

    this.assistantBuffer += chunk;

    // 启动定时刷新，而不是每次追加都触发渲染
    if (!this.assistantTimer) {
      this.assistantTimer = setTimeout(() => {
        this.assistantTimer = null;
        this.flushAssistant(false);
      }, this.config.streamFlushInterval);
    }
  }

  /**
   * 刷新 Assistant 缓冲区内容到全局 Store
   * @param finalize 是否为最后一次刷新（标记结束）
   */
  private flushAssistant(finalize: boolean) {
    const pending = this.assistantBuffer;
    this.assistantBuffer = '';

    const { updateMessageById, appendMessage } = useChatStore.getState();
    const now = new Date();

    // 如果还没有对应的消息 ID，则先创建一条空消息
    if (!this.assistantId) {
      this.assistantId = `assistant-${now.getTime()}`;
      appendMessage({
        id: this.assistantId,
        role: 'assistant',
        content: '',
        timestamp: now,
        streaming: true,
      });
    }

    // 更新消息内容，处理截断提示
    updateMessageById(this.assistantId, (msg) => {
      const res = appendWithMaxAndNotice(
        msg.content,
        pending,
        this.config.maxStreamingChars,
        '...(content truncated due to length)',
      );
      if (res.truncated) this.assistantTruncated = true;
      return {
        ...msg,
        content: res.content,
        timestamp: now,
        streaming: !finalize,
      };
    });
  }

  /**
   * 外部调用：追加 Log 运行日志
   * @param rawChunk 任意类型的原始数据
   */
  public appendLogOutput(rawChunk: unknown) {
    const chunk = stringifyClaudeOutput(rawChunk);
    if (!chunk?.trim()) return;

    this.logBuffer.push(chunk);

    // 批量合并日志，降低渲染压力
    if (!this.logTimer) {
      this.logTimer = setTimeout(() => {
        this.logTimer = null;
        this.flushLog();
      }, this.config.logFlushInterval);
    }

    // 自动判定流结束：如果 400ms 内没有新日志进来，自动关闭 streaming 状态
    if (this.logEndTimer) clearTimeout(this.logEndTimer);
    this.logEndTimer = setTimeout(() => {
      this.finishLog();
    }, 400);
  }

  /**
   * 刷新 Log 缓冲区内容到全局 Store
   */
  private flushLog() {
    if (this.logBuffer.length === 0) return;
    const chunk = this.logBuffer.join('\n');
    this.logBuffer = [];

    const { updateMessageById, appendMessage } = useChatStore.getState();
    const now = new Date();

    // 首次输出创建 Log 类型消息
    if (!this.logId) {
      this.logId = `log-${now.getTime()}`;
      appendMessage({
        id: this.logId,
        role: 'log',
        content: trimTailWithNotice(chunk, this.config.maxLogChars, '\n...(log truncated)\n'),
        timestamp: now,
        streaming: true,
      });
      return;
    }

    // 追加到现有 Log 消息
    updateMessageById(this.logId, (msg) => ({
      ...msg,
      content: trimTailWithNotice(
        msg.content ? `${msg.content}\n${chunk}` : chunk,
        this.config.maxLogChars,
        '\n...(log truncated)\n',
      ),
      timestamp: now,
    }));
  }

  /**
   * 结束当前正在累加的 Log 消息
   */
  private finishLog() {
    if (this.logId) {
      const id = this.logId;
      this.logId = null;
      useChatStore.getState().updateMessageById(id, (msg) => ({ ...msg, streaming: false }));
    }
  }

  /**
   * 外部调用：追加折叠块输出 (Collapsible)
   * 常用于处理如 Shell 执行结果等大块内容，会自动按时间戳切分或合并
   */
  public appendCollapsibleOutput(rawData: unknown, rawTimestamp: unknown = null) {
    const content = stringifyClaudeOutput(rawData).trim();
    if (!content) return;

    const nowMs = rawTimestamp
      ? new Date(rawTimestamp as string | number | Date).getTime()
      : Date.now();
    // 判断是否应该开启新折叠块：如果距离上次更新过久，或者是首次输出
    const shouldStartNew =
      !this.collapsibleId || nowMs - this.collapsibleLastUpdate > this.COLLAPSIBLE_RESET_MS;
    this.collapsibleLastUpdate = nowMs;

    const { appendMessage, updateMessageById } = useChatStore.getState();
    const ts = new Date(nowMs);

    if (shouldStartNew) {
      this.collapsibleId = `collapsible-${nowMs}-${Math.random().toString(36).slice(2, 8)}`;
      appendMessage({
        id: this.collapsibleId,
        role: 'system',
        content: '',
        collapsibleRaw: content,
        timestamp: ts,
        messageType: 'collapsible',
      });
    } else {
      updateMessageById(this.collapsibleId!, (msg) => ({
        ...msg,
        collapsibleRaw: trimTailWithNotice(
          msg.collapsibleRaw ? `${msg.collapsibleRaw}\n${content}` : content,
          this.config.maxCollapsibleChars,
          '\n...(output truncated)\n',
        ),
        timestamp: ts,
      }));
    }
  }

  /**
   * 强制结束当前所有流式更新并清空缓冲区
   * 常在收到 'claude-complete' 或 'claude-error' 时调用
   */
  public finishAll() {
    // 强制刷新文本流并清空状态
    if (this.assistantTimer) clearTimeout(this.assistantTimer);
    if (this.assistantId) {
      this.flushAssistant(true);
      this.assistantId = null;
    }
    this.assistantBuffer = '';
    this.assistantTruncated = false;

    // 强制刷新日志流并清空状态
    if (this.logTimer) clearTimeout(this.logTimer);
    if (this.logEndTimer) clearTimeout(this.logEndTimer);
    this.flushLog();
    this.finishLog();

    // 重置折叠块标识
    this.collapsibleId = null;
  }

  /**
   * 彻底重置管理器（应用级清理）
   */
  public clear() {
    this.finishAll();
  }
}

/** 导出单例，确保全应用公用一套流缓冲区 */
export const streamingManager = new StreamingManager();
